/**
 * changedPhraseKit.ts — PLAT-10265 (epic PLAT-10209)
 *
 * Pure, surface-agnostic helpers for the changed-phrase ("Fix Translation")
 * treatment, shared by both translated transcript surfaces: the review modal's
 * aligned view (`AlignedBlocksView`) and the prose renderer
 * (`TranscriptionTextTranslated`). Extracted here so the prose renderer never
 * has to import from the modal component — one source of truth, no coupling.
 *
 * Behavior-identical extraction (A1): both functions are moved verbatim from
 * `AlignedBlocksView.tsx`, same signatures and logic. No styling, no component,
 * no wiring lives here.
 */

import { joinFragments } from '@src/stores-v2/translationSpanEdit';
import { getPatch } from 'fast-array-diff';
import { css } from 'styled-components';

import {
  isEdited as isPhraseEdited,
  resolveOriginalValue,
} from './translatedPhraseEditing';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type {
  ParentPhrase,
  TranslationChange,
  TranslationPhrase,
  TranslationSegment,
} from '@src/types.ts/translation';

/**
 * The green tint for the changed-phrase mark. Shared by the whole-word
 * background (`changedPhraseHighlightCss`) and the per-word mark used when only
 * part of a "Fix Translation" correction changed (`resolveChangedWordTokens`
 * consumers, PLAT-10270), so both surfaces stay the exact same green.
 */
export const CHANGED_PHRASE_TINT =
  'color-mix(in srgb, var(--primary) 22%, transparent)';

/**
 * The changed-phrase ("edited") green-highlight rule for a translated phrase
 * `<Word>` (PLAT-10265). Interpolate into a `styled(Word)` wrapper — `&` resolves
 * to that wrapper. The green tint (`--primary` at 22%) survives the PAUSED
 * caret (`.edited.current.cursor-before`) and the resting edited state
 * (`.edited:not(.current)`); the PLAYING playhead's gold fill (`.current` without
 * `.cursor-before`) takes over the background, so this deliberately does not match
 * that state. A different hue from the gold playhead and the blue linked-selection
 * highlight so the states never read as the same thing.
 */
export const changedPhraseHighlightCss = css`
  &.edited:not(.current),
  &.edited.current.cursor-before {
    background-color: ${CHANGED_PHRASE_TINT};
    border-radius: 3px;
  }
`;

/** A fragment is TM-backed when the backend stamped at least one honored
 *  translation-memory entry id onto it (PLAT-10850, consuming PLAT-10843). */
export function isTmBacked(phrase: TranslationPhrase): boolean {
  return (phrase.tmEntryIds?.length ?? 0) > 0;
}

/**
 * Stable identity for a fragment across the review surfaces (PLAT-10852): its
 * source indices. Used to remember which fragment a reviewer just promoted into
 * a translation-memory entry, so the overlay, the Changes cards and the TM cards
 * all agree on "this one has an entry now" — the backend `tmEntryIds` only
 * appears after a re-translate, so a just-promoted fragment has no other link
 * back to its entry.
 */
export function fragmentKeyOf(originalIndices: readonly number[]): string {
  return originalIndices.join(',');
}

/**
 * The promote/link key for a phrase as the EDIT OVERLAY sees it (PLAT-10852).
 *
 * A span-replace override is one authored unit: its Changes card keys off the
 * override's own span (`seg.parent.originalIndices`), so the overlay — which is
 * opened on an individual derived fragment — must resolve back to that same
 * span. Otherwise the two surfaces disagree about whether the unit is already an
 * entry, and the overlay keeps offering to promote text the reviewer just
 * promoted from the card.
 *
 * Only a real override (`o_…`) redirects. A `p_…` parent is a backend split
 * group, not an authored unit — the same disambiguation `usePhraseEditing`
 * makes for revert.
 */
export function overlayPromoteKey(
  phrase: TranslationPhrase,
  parentPhrases: readonly ParentPhrase[] | undefined,
): string {
  const overrideSpan = phrase.parentId?.startsWith('o_')
    ? parentPhrases?.find(p => p.id === phrase.parentId)?.originalIndices
    : undefined;
  return fragmentKeyOf(overrideSpan ?? phrase.originalIndices);
}

/**
 * The ACTIVE phrases a promote key refers to (PLAT-10852) — the fragments that
 * must carry the new entry's id so every surface agrees the unit is an entry.
 *
 * The three promote sites don't all key the same way: the edit overlay and the
 * Changes cards key an override by its authored span ({@link overlayPromoteKey}),
 * while a TM card keys the individual derived fragment it renders
 * ({@link fragmentKeyOf} of that fragment). Accepting BOTH here is what lets one
 * durable stamp satisfy all of them: a span key resolves to every derived
 * fragment of the override, a fragment key to just that fragment, and a plain
 * (unsplit) fragment answers to the single key both forms produce.
 */
export function phrasesForPromoteKey(
  fragmentKey: string,
  phrases: readonly TranslationPhrase[] | undefined,
  parentPhrases: readonly ParentPhrase[] | undefined,
): TranslationPhrase[] {
  return (phrases ?? []).filter(
    p =>
      p.active !== false &&
      (fragmentKeyOf(p.originalIndices) === fragmentKey ||
        overlayPromoteKey(p, parentPhrases) === fragmentKey),
  );
}

/**
 * What a surface hands the host when the reviewer promotes a translation into a
 * glossary entry (PLAT-10852). One shape for all three promote sites (the edit
 * overlay, the Changes cards, the TM cards) so they can't drift apart.
 */
export interface TmPromoteSeed {
  /** {@link fragmentKeyOf} of the promoted fragment — for an override, of the
   *  override's own span, so every surface keys the same unit. */
  fragmentKey: string;
  /** Source-language text to seed the entry's `source_term`. */
  sourceTerm: string;
  /** The translation being promoted — the value on screen, not the stored one. */
  translation: string;
}

/**
 * Resolve the translation-memory entry behind a fragment, or `null`. Prefers the
 * backend's honored provenance (`tmEntryIds`), then a promotion made in this
 * session (`promotedByFragment`). Shared so every surface answers "is this a
 * translation-memory entry?" the same way.
 */
export function resolveFragmentTmEntry<T>(
  fragmentKey: string,
  tmEntryIds: readonly string[] | undefined,
  entriesById: ReadonlyMap<string, T> | undefined,
  promotedByFragment: ReadonlyMap<string, string> | undefined,
): T | null {
  for (const id of tmEntryIds ?? []) {
    const entry = entriesById?.get(id);
    if (entry) return entry;
  }
  const promotedId = promotedByFragment?.get(fragmentKey);
  return (promotedId && entriesById?.get(promotedId)) || null;
}

/**
 * How many DISTINCT translation-memory entries a run honored (PLAT-10853) —
 * the run-level adoption number, as opposed to {@link countTmBackedFragments}'s
 * per-fragment count. One entry matched in twenty sentences is one entry.
 * Takes the flat phrase array off a translation blob (not segments), because
 * the analytics call site reads the translation straight out of the store
 * before any segment view exists.
 */
export function countDistinctTmEntries(
  phrases: readonly TranslationPhrase[] | undefined,
): number {
  if (!phrases?.length) return 0;
  const ids = new Set<string>();
  for (const phrase of phrases) {
    for (const id of phrase.tmEntryIds ?? []) ids.add(id);
  }
  return ids.size;
}

/**
 * Locate the glossary TERM occurrences inside a translated fragment so the UI
 * underlines ONLY the dictionary word(s), not the whole fragment (PLAT-10850).
 * A production fragment is usually a whole sentence and the glossary term is one
 * word inside it, so a per-fragment mark over-underlines — this finds the term
 * spans to mark instead. Case-insensitive substring match of each term with a
 * Unicode letter-boundary guard (so "Toro" doesn't match inside "Motoro", and
 * accents are handled — unlike ASCII `\b`). `terms` are the resolved entry
 * strings (the target term, or the source term for a do-not-translate entry).
 * Returns merged, sorted `[start, end)` ranges into `text`; empty when nothing
 * matches (the caller then renders the fragment with no mark).
 */
export function findTmTermRanges(
  text: string,
  terms: readonly string[],
): Array<[number, number]> {
  const isLetter = (c: string): boolean => c !== '' && /\p{L}/u.test(c);
  const lower = text.toLowerCase();
  const ranges: Array<[number, number]> = [];
  for (const raw of terms) {
    const term = raw.trim().toLowerCase();
    if (!term) continue;
    let from = 0;
    for (;;) {
      const idx = lower.indexOf(term, from);
      if (idx < 0) break;
      const end = idx + term.length;
      const before = idx > 0 ? text[idx - 1] : '';
      const after = end < text.length ? text[end] : '';
      if (!isLetter(before) && !isLetter(after)) ranges.push([idx, end]);
      from = idx + term.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [start, end] of ranges) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
}

/**
 * The first SEEKABLE source word behind a set of source indices — the earliest
 * index that resolves to a `text` element with a numeric `ts`. Drives both the
 * mm:ss label and the click-to-seek target in the Translation Review panels
 * (punctuation / removed words have no usable timestamp, same rule as
 * `seekToInitialIndex`). Shared by `TranslationChangesPanel` and
 * `TranslationMemoryPanel` (PLAT-10850 DRY).
 */
export function resolveSeekTarget(
  originalIndices: readonly number[],
  byIndex: Map<number, TranscriptElement>,
): { index: number; ts: number } | undefined {
  for (const idx of [...originalIndices].sort((a, b) => a - b)) {
    const el = byIndex.get(idx);
    if (el && el.type === 'text' && typeof el.ts === 'number') {
      return { index: el.initial_index, ts: el.ts };
    }
  }
  return undefined;
}

/**
 * Class string for a translated phrase `<Word>` in the aligned view. Combines
 * the playhead state (`current` / `current cursor-before`, PLAT-10263) with the
 * changed-phrase mark (`edited`, PLAT-10265) and the stale mark (`stale`,
 * PLAT-10266). The green background survives the PAUSED caret (a click/seek onto
 * the phrase) and is taken over only by the PLAYING playhead's gold fill — see
 * `TranslatedWord` for the CSS; the `edited` class is emitted here regardless so
 * the mark is always present. `isStale` defaults to `false`, so existing 3-arg
 * callers are unchanged (they never emit `stale`).
 */
export function translatedPhraseClassName(
  isCurrent: boolean,
  isPlaying: boolean,
  isEdited: boolean,
  isStale = false,
): string | undefined {
  const classes: string[] = [];
  if (isCurrent) {
    classes.push('current');
    if (!isPlaying) classes.push('cursor-before');
  }
  if (isEdited) classes.push('edited');
  if (isStale) classes.push('stale');
  return classes.length > 0 ? classes.join(' ') : undefined;
}

/**
 * Map each override segment id → the machine-original translation it replaced
 * (the retired fragments it superseded, joined in source order). A span-replace
 * override's derived fragments carry `isUserEdited: true` but NO per-fragment
 * `originalTranslatedValue` — so the changed-phrase hover can't read the
 * previous value off the phrase itself. It reads it from here instead, and Undo
 * reverts the whole override (PLAT-10265). Single-phrase edits don't use this —
 * their previous value is the phrase's own `originalTranslatedValue` (see
 * `resolveOriginalValue`). Empty joins are dropped so a lookup miss is honest.
 */
export function buildOverrideOriginalMap(
  phrases: readonly TranslationPhrase[],
): Map<string, string> {
  const retiredByOverride = new Map<string, TranslationPhrase[]>();
  for (const p of phrases) {
    if (p.supersededBy === undefined) continue;
    const list = retiredByOverride.get(p.supersededBy);
    if (list) list.push(p);
    else retiredByOverride.set(p.supersededBy, [p]);
  }
  const byId = new Map<string, string>();
  for (const [id, retired] of retiredByOverride) {
    const original = joinFragments(retired);
    if (original) byId.set(id, original);
  }
  return byId;
}

/** The changed-phrase resolution for one translated phrase — see {@link resolveChangedPhrase}. */
export interface ChangedPhraseResolution {
  /** True when the applied translation differs from the machine baseline (or,
   *  when that baseline can't be resolved, when `isUserEdited` is set). */
  isEdited: boolean;
  /** The span-replace override segment id (`o_…`) this fragment belongs to, or
   *  `undefined` for a single-phrase edit / unedited phrase. */
  overrideId: string | undefined;
  /** The previous (machine) value for the hover tooltip — from the override's
   *  retired originals (`overrideMap`) or the phrase's own machine baseline. */
  previousValue: string | undefined;
  /** Attach the hover tooltip only when a previous value is known (so it never
   *  opens empty); the green mark shows regardless. */
  showChangedTooltip: boolean;
  /**
   * The changed-phrase data attributes for the word anchor: `data-prev-value`
   * plus exactly one undo target — `data-override-id` (revert the whole
   * span-replace override) or `data-oi` (revert a single phrase by its indices).
   * Empty when there's no tooltip-worthy edit. EXCLUDES `data-tooltip-id` — the
   * caller owns the tooltip anchor id (the prose surface uses its own).
   */
  dataAttrs: Record<string, string | undefined>;
}

/**
 * Resolve one translated phrase's changed-phrase mark + hover-previous + Undo
 * wiring (PLAT-10265). Pure function of the phrase, the whole-story phrase array
 * (for the machine-baseline lookup), and the override→original map. Moved verbatim
 * from `AlignedBlocksView`'s translated-column render so both the modal and the
 * prose surface share one source of truth.
 *
 * A span-replace OVERRIDE fragment (`parentId` `o_…`) has no per-fragment machine
 * original; its previous value is the override's retired originals (`overrideMap`),
 * and Undo reverts the whole override — not a single phrase.
 */
export function resolveChangedPhrase(
  phrase: TranslationPhrase,
  allPhrases: readonly TranslationPhrase[] | undefined,
  overrideMap: Map<string, string>,
): ChangedPhraseResolution {
  const isEdited = isPhraseEdited(phrase, allPhrases);
  const overrideId =
    isEdited && phrase.parentId?.startsWith('o_') ? phrase.parentId : undefined;
  const previousValue = !isEdited
    ? undefined
    : overrideId
      ? overrideMap.get(overrideId)
      : resolveOriginalValue(phrase);
  const showChangedTooltip = isEdited && previousValue !== undefined;
  const dataAttrs: Record<string, string | undefined> = showChangedTooltip
    ? {
        'data-prev-value': previousValue,
        ...(overrideId
          ? { 'data-override-id': overrideId }
          : { 'data-oi': phrase.originalIndices.join(',') }),
      }
    : {};
  return { isEdited, overrideId, previousValue, showChangedTooltip, dataAttrs };
}

/** One render token from {@link resolveChangedWordTokens}: either a single word
 *  or a run of whitespace. `changed` is set only on words that differ from the
 *  machine baseline; whitespace runs are never marked. */
export interface ChangedWordToken {
  text: string;
  changed: boolean;
}

/**
 * Break an edited phrase's applied translation into word-level tokens, flagging
 * only the words that actually differ from the machine baseline (PLAT-10270).
 *
 * A span-replace "Fix Translation" correction is stored as ONE fragment
 * covering the whole edited span — a free rewrite can't be source-aligned, so
 * the on-demand split keeps it whole (`TranslationStore.resplitOverrideSegment`).
 * The green "edited" mark is per-fragment, so a one-word edit would otherwise
 * paint the whole segment green. This compares the correction against its
 * machine baseline (`resolveOriginalValue`, which for an override joins the
 * retired per-word originals) with a word diff and flags only the
 * inserted/replaced words.
 *
 * Whitespace runs are returned as their own never-changed tokens so the caller
 * re-renders the exact original spacing. When the baseline can't be resolved,
 * the whole value is returned as a single token whose `changed` mirrors the
 * `isEdited` fallback (`isUserEdited`).
 */
export function resolveChangedWordTokens(
  phrase: TranslationPhrase,
  allPhrases: readonly TranslationPhrase[] | undefined,
): ChangedWordToken[] {
  const value = phrase.translatedValue;
  const baseline = resolveOriginalValue(phrase, allPhrases);
  if (baseline === undefined) {
    return [{ text: value, changed: phrase.isUserEdited === true }];
  }
  // Split into alternating word / whitespace pieces so re-rendering preserves
  // the exact interior spacing; only real words take part in the diff.
  const isSpace = (piece: string): boolean => /^\s+$/.test(piece);
  const pieces = value.split(/(\s+)/).filter(piece => piece.length > 0);
  const currentWords = pieces.filter(piece => !isSpace(piece));
  const baselineWords = baseline.split(/\s+/).filter(word => word.length > 0);

  // `getPatch(baseline, current)` yields the edit script from the machine text
  // to the applied text; every `add` item names words present in the applied
  // text but not matched in the baseline — i.e. the changed/inserted words.
  const changedWordIndices = new Set<number>();
  for (const item of getPatch(baselineWords, currentWords)) {
    if (item.type !== 'add') continue;
    for (let offset = 0; offset < item.items.length; offset += 1) {
      changedWordIndices.add(item.newPos + offset);
    }
  }

  let wordIndex = 0;
  return pieces.map(piece => {
    if (isSpace(piece)) return { text: piece, changed: false };
    const changed = changedWordIndices.has(wordIndex);
    wordIndex += 1;
    return { text: piece, changed };
  });
}

/**
 * Whether an edited phrase's ONLY change is that words were deleted
 * (PLAT-11340 follow-up).
 *
 * Why it needs asking: {@link resolveChangedWordTokens} marks the words the
 * diff reports as `add` — words present in the applied text but not the
 * baseline. A pure deletion produces only `remove` items, so it marks nothing,
 * `partialChange` is false, and the consumer falls back to the WHOLE-phrase
 * green. That fallback is how removing two words from a sentence painted the
 * entire sentence as changed: the green was never chosen for it, it was just
 * what was left.
 *
 * Callers render deletions their own way instead (the review workspace scores
 * the row and leaves the text untinted). Derived from the same diff the green
 * uses — no stored flag, so a deletion typed by hand into "Fix Translation"
 * reads identically to one made with "Remove Translation".
 *
 * Gated on `isPhraseEdited` first: for an UNEDITED phrase the baseline IS the
 * current value, so the diff reports no adds and this would answer true for
 * every untouched phrase on screen. A phrase emptied outright yields no tokens
 * at all and is deliberately excluded — that case has its own struck-baseline
 * rendering.
 */
export function isDeletionOnlyChange(
  phrase: TranslationPhrase,
  allPhrases?: readonly TranslationPhrase[],
): boolean {
  if (!isPhraseEdited(phrase, allPhrases)) return false;
  const tokens = resolveChangedWordTokens(phrase, allPhrases);
  return tokens.length > 0 && !tokens.some(token => token.changed);
}

/**
 * Provenance label for a machine-steered ("directed") override segment —
 * `AI re-translated · "<instruction>"`, or plain `AI re-translated` when the
 * steering text is unknown; `undefined` for every other origin so each surface
 * keeps its existing manual wording (PLAT-10257 A5, AC4 of the ticket).
 *
 * Driven by stored data only: `origin === 'directed'` on the override segment
 * selects the label, and the instruction comes off the changelog entry joined
 * via `parentId`. The LAST matching entry describes the current wording
 * (`TranslationStore.withChange` appends) — so a later manual re-edit of a
 * directed segment, whose entry carries no instruction, degrades the label to
 * plain "AI re-translated" rather than showing a stale instruction. The
 * no-instruction case is also the PLAT-10266 forward-compatibility seam
 * (stale re-translate authors directed-origin segments without one).
 */
export function resolveProvenanceLabel(
  parent: Pick<ParentPhrase, 'id' | 'origin'>,
  changelog: readonly TranslationChange[] | undefined,
): string | undefined {
  if (parent.origin !== 'directed') return undefined;
  let instruction: string | undefined;
  if (changelog) {
    for (let i = changelog.length - 1; i >= 0; i -= 1) {
      if (changelog[i].parentId === parent.id) {
        instruction = changelog[i].instruction;
        break;
      }
    }
  }
  return instruction
    ? `AI re-translated · "${instruction}"`
    : 'AI re-translated';
}

/**
 * Count the ACTIVE changed entries for a language — one per span-replace
 * override segment plus one per edited single phrase — matching exactly what
 * `TranslationChangesPanel` lists as cards. Pure; pass `store.getSegments(lang)`
 * and the translation's `phrases`. Used for the "Changes (N)" tab badge so the
 * count stays live even when the Changes tab isn't mounted (PLAT-10702). Keep in
 * lockstep with the panel's entry-build rule.
 */
export function countActiveChanges(
  segments: readonly TranslationSegment[],
  phrases: readonly TranslationPhrase[],
): number {
  const overrideMap = buildOverrideOriginalMap(phrases);
  let count = 0;
  for (const seg of segments) {
    if ((seg.parent?.id.startsWith('o_') ?? false) && seg.parent) {
      count++;
      continue;
    }
    for (const fragment of seg.fragments) {
      if (resolveChangedPhrase(fragment, phrases, overrideMap).isEdited)
        count++;
    }
  }
  return count;
}

/**
 * The translation-memory entry ids in effect for a fragment: the backend's
 * honored `tmEntryIds` when it has them, otherwise the entry the reviewer
 * promoted this fragment into during this session (PLAT-10852). Empty ⇒ not a
 * glossary term. The session fallback is what makes a just-added term visible
 * before a re-translate — the backend stamps `tmEntryIds` only then.
 */
export function tmEntryIdsOf(
  phrase: TranslationPhrase,
  promotedByFragment?: ReadonlyMap<string, string>,
): readonly string[] {
  if (phrase.tmEntryIds?.length) return phrase.tmEntryIds;
  const promotedId = promotedByFragment?.get(
    fragmentKeyOf(phrase.originalIndices),
  );
  return promotedId ? [promotedId] : [];
}

/**
 * Count the ACTIVE TM-backed fragments for a language — one per fragment with
 * translation-memory ids in effect ({@link tmEntryIdsOf}, so a fragment promoted
 * this session counts too) — matching exactly what `TranslationMemoryPanel`
 * lists as cards. Pure; pass `store.getSegments(lang)` (whose `fragments` are
 * already the active fragments). Used for the "TM (N)" tab badge so the count
 * stays live even when the TM tab isn't mounted (mirrors
 * {@link countActiveChanges}). Keep in lockstep with the panel's entry-build
 * rule (PLAT-10850/10852).
 */
export function countTmBackedFragments(
  segments: readonly TranslationSegment[],
  promotedByFragment?: ReadonlyMap<string, string>,
): number {
  let count = 0;
  for (const seg of segments) {
    for (const fragment of seg.fragments) {
      if (tmEntryIdsOf(fragment, promotedByFragment).length) count++;
    }
  }
  return count;
}
